import { useEffect, useState } from "react";
import * as THREE from "three";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const swatchUrl = (s) => (s && s.startsWith("/api") ? `${BASE}${s}` : s);

const cache = {};
export const loadImage = (url) => {
  if (!url) return Promise.resolve(null);
  if (!cache[url]) {
    cache[url] = new Promise((res) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = () => res(i);
      i.onerror = () => res(null);
      i.src = url;
    });
  }
  return cache[url];
};

const rng = (seed) => { let s = seed % 2147483647 || 1; return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; }; };

function makeTile(g, img, col, ppm, gr, rand) {
  return (x, y, w, h) => {
    const ix = x + gr / 2, iy = y + gr / 2, iw = w - gr, ih = h - gr;
    if (iw <= 0 || ih <= 0) return;
    if (img) {
      const sm = col.swatch_m || 1.2;
      const cw = img.width * Math.min(1, w / ppm / sm);
      const ch = img.height * Math.min(1, h / ppm / sm);
      g.drawImage(img, rand() * (img.width - cw), rand() * (img.height - ch), cw, ch, ix, iy, iw, ih);
    } else {
      g.fillStyle = col.colour || "#ddd";
      g.fillRect(ix, iy, iw, ih);
    }
    const v = rand();
    g.fillStyle = v > 0.5 ? `rgba(255,255,255,${(v - 0.5) * 0.12})` : `rgba(0,0,0,${v * 0.1})`;
    g.fillRect(ix, iy, iw, ih);
    g.strokeStyle = "rgba(0,0,0,0.10)";
    g.lineWidth = 1;
    g.strokeRect(ix + 0.5, iy + 0.5, iw - 1, ih - 1);
  };
}

function layTiles(g, tile, spec, ppm, W, top, H) {
  const tw = (spec.tile_w / 1000) * ppm;
  const th = (spec.tile_h / 1000) * ppm;
  const pat = spec.pattern || "grid";
  if (pat === "herringbone") {
    const Lg = Math.max(tw, th), S = Math.min(tw, th);
    const R = Math.hypot(W, H - top) / 2 + Lg;
    g.save();
    g.translate(W / 2, (top + H) / 2);
    g.rotate(Math.PI / 4);
    const n = Math.ceil((2 * R) / Lg) + 2, m = Math.ceil((2 * R) / S) + 2;
    for (let s = -n; s <= n; s++) {
      for (let k = -m; k <= m; k++) {
        const x = k * S + s * Lg, y = k * S - s * Lg;
        if (Math.abs(x) > R + Lg || Math.abs(y) > R + Lg) continue;
        tile(x, y, Lg, S);
        tile(x, y + S, S, Lg);
      }
    }
    g.restore();
    return;
  }
  const w = pat === "stack_vertical" ? Math.min(tw, th) : tw;
  const h = pat === "stack_vertical" ? Math.max(tw, th) : th;
  for (let r = 0; H - r * h > top - h; r++) {
    const off = pat === "brick" && r % 2 ? -w / 2 : 0;
    for (let x = off; x < W; x += w) tile(x, H - (r + 1) * h, w, h);
  }
}

export function drawSurface({ wM, hM, spec, col, img, paint }) {
  const ppm = Math.min(300, 2048 / Math.max(wM, hM));
  const W = Math.max(2, Math.round(wM * ppm));
  const H = Math.max(2, Math.round(hM * ppm));
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const g = c.getContext("2d");
  g.fillStyle = paint || "#ece6dc";
  g.fillRect(0, 0, W, H);
  if (!col) return c;
  const tiledM = spec.tiled_height ? Math.min(hM, spec.tiled_height) : hM;
  const top = H - tiledM * ppm;
  g.save();
  g.beginPath();
  g.rect(0, top, W, H - top);
  g.clip();
  if (col.swatch_mode === "pattern" && img) {
    const s = (col.swatch_m || 1) * ppm;
    for (let y = H; y > top - s; y -= s) for (let x = 0; x < W; x += s) g.drawImage(img, x, y - s, s, s);
  } else {
    g.fillStyle = spec.grout_colour || "#bdb8ae";
    g.fillRect(0, top, W, H - top);
    const gr = Math.max(0.8, ((spec.grout_mm || 2) / 1000) * ppm);
    layTiles(g, makeTile(g, img, col, ppm, gr, rng(Math.round(wM * 1000 + hM * 7))), spec, ppm, W, top, H);
  }
  g.restore();
  if (spec.tiled_height && tiledM < hM) {
    g.fillStyle = "rgba(201,169,110,0.9)";
    g.fillRect(0, top - 2, W, 3);
  }
  return c;
}

export function useSurfaceTexture({ wM, hM, spec, col, paint }) {
  const [tex, setTex] = useState(null);
  const key = JSON.stringify([wM, hM, spec, col?.id, col?.swatch, col?.colour, col?.swatch_mode, col?.swatch_m, paint]);
  useEffect(() => {
    let alive = true;
    loadImage(swatchUrl(col?.swatch)).then((img) => {
      if (!alive) return;
      const t = new THREE.CanvasTexture(drawSurface({ wM, hM, spec, col, img, paint }));
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
      setTex((old) => { if (old) old.dispose(); return t; });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return tex;
}

export const roughnessFor = (col) => {
  const f = (col?.finish || "").toLowerCase();
  if (f.includes("polish") || f.includes("gloss")) return 0.18;
  if (f.includes("hone")) return 0.45;
  return col ? 0.75 : 0.95;
};
